import { createJourneyLayerController, type JourneyLayerController } from './journey-layer-controller';
import { createMapCameraController, type MapCameraController, type MapCameraMarker } from './map-camera-controller';
import type { LayerGroupApply, MapRuntimeBindings, MapRuntimePort } from './map-runtime';
import { createRouteLayerController, type RouteLayerController } from './route-layer-controller';
import {
  createTerrainLayerController,
  type TerrainLayerController,
  type TerrainLayerDependencies,
  type TerrainLayerEvents
} from './terrain-layer-controller';
import {
  createWeatherLayerController,
  type WeatherLayerController,
  type WeatherLayerDependencies,
  type WeatherLayerEvents
} from './weather-layer-controller';

export interface MapMarkerHandle extends MapCameraMarker {}

export interface MapAdapter {
  readonly camera: MapCameraController;
  readonly routes: RouteLayerController;
  readonly journey: JourneyLayerController;
  readonly weather: WeatherLayerController;
  readonly terrain: TerrainLayerController;
  setStyle(styleUrl: string): void;
}

export interface MapAdapterDependencies {
  runtimeBindings?: MapRuntimeBindings;
  weather?: WeatherLayerDependencies;
  weatherEvents?: WeatherLayerEvents;
  terrain?: Omit<TerrainLayerDependencies, 'camera'>;
  terrainEvents?: TerrainLayerEvents;
}

export interface OwnedMapAdapter extends MapAdapter {
  dispose(): void;
}

export function createMapAdapter(runtime: MapRuntimePort, dependencies: MapAdapterDependencies = {}): OwnedMapAdapter {
  let epoch = 0;
  let disposed = false;
  const layerGroups = new Map<string, LayerGroupApply>();

  const registerLayerGroup = (id: string, apply: () => void | Promise<void>) => {
    const ownedApply: LayerGroupApply = () => apply();
    layerGroups.set(id, ownedApply);
    return () => {
      if (layerGroups.get(id) === ownedApply) layerGroups.delete(id);
    };
  };

  const onStyleLoad = () => {
    if (disposed) return;
    epoch += 1;
    layerGroups.forEach((apply) => void apply(runtime, epoch));
  };
  runtime.on('style.load', onStyleLoad);

  const bindings = dependencies.runtimeBindings;
  const camera = createMapCameraController(
    runtime,
    (element, coordinate) => bindings?.createMarker?.(runtime, element, coordinate) ?? null
  );
  const routes = createRouteLayerController(runtime, registerLayerGroup);
  const journey = createJourneyLayerController(runtime, registerLayerGroup, {
    showPopup: ({ coordinate, html, offset }) => bindings?.showPopup?.(runtime, coordinate, html, offset)
  });
  const weather = createWeatherLayerController(
    runtime,
    registerLayerGroup,
    dependencies.weather,
    dependencies.weatherEvents
  );
  const terrain = createTerrainLayerController(
    runtime,
    registerLayerGroup,
    { ...dependencies.terrain, camera },
    dependencies.terrainEvents
  );

  return {
    camera,
    routes,
    journey,
    weather,
    terrain,
    setStyle(styleUrl) { runtime.setStyle(styleUrl); },
    dispose() {
      if (disposed) return;
      disposed = true;
      terrain.dispose();
      weather.dispose();
      journey.dispose();
      routes.dispose();
      runtime.off?.('style.load', onStyleLoad);
      layerGroups.clear();
    }
  };
}
